import { unified } from 'unified';
import remarkParse from 'remark-parse';
import strip from 'strip-markdown';
import { toString } from 'mdast-util-to-string';

export interface IGrouping<TKey, TElement> {
  key: TKey;
  elements: TElement[];
}

export interface StringCoalesceToMaxSizeOptions {
  addEllipsis?: boolean; // defaults to false
}

/**
 * Returns fallback if value is null, undefined, empty, or whitespace-only.
 * Useful for safely displaying optional string properties with a fallback.
 */
export function nullOrEmptyCoalesce(value: string | null | undefined, fallback: string): string {
  if (value == null) return fallback;
  return value.trim().length === 0 ? fallback : value;
}

declare global {
  interface Array<T> {
    selectMany<U>(projection: (item: T) => U[]): U[];
    distinct(projection: (item: T) => string): T[];
    reorder(startIndex: number, endIndex: number): T[];
    firstOrDefault(predicate?: (item: T) => boolean): T | null;
    max(projection: (item: T) => number): number | null;
    maxOrDefault(projection: (item: T) => number, defaultValue: number): number;
    min(projection: (item: T) => number): number | null;
    minOrDefault(projection: (item: T) => number, defaultValue: number): number;
    skip(n: number): T[];
    take(n: number): T[];
    orderBy<K>(projection: ((a: T) => K)): T[];
    orderByDescending<K>(projection: ((a: T) => K)): T[];
    groupBy(projection: ((a: T) => string)): IGrouping<string, T>[];
    groupByNumber<K extends number>(projection: ((a: T) => K)): IGrouping<K, T>[];
    emptyCoalesce(arrayToUse: T[]): T[];
    arrayJoin(separator: T): T[];
  }

  interface Date {
    getNearestPastMonday(): Date;
    addDays(days: number): Date;
    startOfDay(): Date;
    isSameDayAs(otherDate: Date): boolean;
  }

  interface String {
    emptyCoalesce(value: string): string;
    coalesceToMaxSize(maxSize: number, options?: StringCoalesceToMaxSizeOptions): string;
    toDisplayName(): string;
  }

  interface Number {
    clamp(minInclusive: number, maxInclusive: number): number;
    roundToDigits(digits: number): number;
  }
}

// // Class to handle the ordering operations
// export class Ordering<T> {
//   private readonly sourceArray: T[];
//   private sortProjections: ((a: T) => any)[];
//
//   constructor(array: T[]) {
//     this.sourceArray = array;
//     this.sortProjections = [];
//   }
//
//   public orderBy(keySelector: (item: T) => any): Ordering<T> {
//     this.sortProjections= [keySelector];
//     return this;
//   }
//
//   public thenBy(keySelector: (item: T) => any): Ordering<T> {
//     this.sortProjections.push(keySelector);
//     return this;
//   }
//
//   public toArray(): T[] {
//     const result = [...this.sourceArray];
//     result.sort((a, b) => {
//       for (const sortProjection of this.sortProjections) {
//         const valueA = sortProjection(a);
//         const valueB = sortProjection(b);
//         if (valueA < valueB) return -1;
//         if (valueA > valueB) return 1;
//       }
//       return 0;
//     });
//     return result;
//   }
// }

Array.prototype.selectMany = function<T, U>(this: T[], projection: (item: T) => U[]): U[] {
  return this.reduce((acc, item) => acc.concat(projection(item)), [] as U[]);
};

Array.prototype.distinct = function<T>(this: T[], projection: (item: T) => string): T[] {
  const seen = new Set<string>();
  return this.filter(item => {
    const key = projection(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

Array.prototype.reorder = function<T>(this: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(this);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

Array.prototype.firstOrDefault = function<T>(this: T[], predicate?: (item: T) => boolean): T | null {
  if (!predicate) {
    return this.length === 0 ? null : this[0];
  }

  for (let item of this) {
    if (predicate(item)) {
      return item;
    }
  }

  return null;
}

Array.prototype.max = function<T>(this: T[], projection: (item: T) => number): number | null {
  const result = this.reduce((previousValue, currentValue) => {
    const val = projection(currentValue);

    return (previousValue === undefined || val > previousValue)
      ? val
      : previousValue;
  }, undefined as (number | undefined));

  return result ?? null;
}

Array.prototype.maxOrDefault = function<T>(this: T[], projection: (item: T) => number, defaultValue: number): number {
  return this.max(projection) ?? defaultValue;
}

Array.prototype.min = function<T>(this: T[], projection: (item: T) => number): number | null {
  const result = this.reduce((previousValue, currentValue) => {
    const val = projection(currentValue);

    return (previousValue === undefined || val < previousValue)
        ? val
        : previousValue;
  }, undefined as (number | undefined));

  return result ?? null;
}

Array.prototype.minOrDefault = function<T>(this: T[], projection: (item: T) => number, defaultValue: number): number {
  return this.min(projection) ?? defaultValue;
}

Array.prototype.skip = function<T>(this: T[], n: number): T[] {
  const result: T[] = [];

  for (let i = n; i < this.length; ++i) {
    result.push(this[i]);
  }

  return result;
}

Array.prototype.take = function<T>(this: T[], n: number): T[] {
  const result: T[] = [];

  for (let i = 0; i < n && i < this.length; ++i) {
    result.push(this[i]);
  }

  return result;
}

Array.prototype.orderBy = function<T, K>(this: T[], projection: ((a: T) => K)): T[] {
  const result = [...this];
  result.sort((a, b) => {
    const valueA = projection(a);
    const valueB = projection(b);
    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
  });
  return result;
}

Array.prototype.orderByDescending = function<T, K>(this: T[], projection: ((a: T) => K)): T[] {
  const result = [...this];
  result.sort((a, b) => {
    const valueA = projection(a);
    const valueB = projection(b);
    if (valueA < valueB) return 1;
    if (valueA > valueB) return -1;
    return 0;
  });
  return result;
}

Array.prototype.groupBy = function<T>(this: T[], projection: ((a: T) => string)): IGrouping<string, T>[] {
  const map = new Map<string, T[]>();

  for (let item of this) {
    const key = projection(item);

    let list = map.get(key);

    if (!list) {
      list = [];
      map.set(key, list);
    }

    list.push(item);
  }

  const result: IGrouping<string, T>[] = [];

  for (let [key, group] of map.entries()) {
    result.push({
      key,
      elements: group
    });
  }

  return result;
}

Array.prototype.groupByNumber = function<T, K extends number>(this: T[], projection: ((a: T) => K)): IGrouping<K, T>[] {
  const map = new Map<K, T[]>();

  for (let item of this) {
    const key = projection(item);

    let list = map.get(key);

    if (!list) {
      list = [];
      map.set(key, list);
    }

    list.push(item);
  }

  const result: IGrouping<K, T>[] = [];

  for (let [key, group] of map.entries()) {
    result.push({
      key,
      elements: group
    });
  }

  return result;
}

Array.prototype.emptyCoalesce = function<T>(this: T[], arrayToUse: T[]): T[] {
  return this.length === 0 ? arrayToUse : this;
}

Array.prototype.arrayJoin = function<T>(this: T[], separator: T): T[] {
  if (this.length === 0) {
    return [];
  }

  const ret: T[] = [this[0]];

  for (let i = 1; i < this.length; ++i) {
    ret.push(separator);
    ret.push(this[i]);
  }

  return ret;
}

Date.prototype.getNearestPastMonday = function(this: Date): Date {
  // In JavaScript, getDay() returns 0 for Sunday, 1 for Monday, ..., 6 for Saturday.
  // Calculate how many days to subtract:
  // If it's Monday (1), (1 - 1) % 7 = 0 days to subtract.
  // For other days, this formula adjusts to give the correct number of days.
  const diff = (7 + (this.getDay() - 1)) % 7;

  return this.addDays(-diff);
}

// Helper to add days to a Date
Date.prototype.addDays = function(this: Date, days: number): Date {
  const result = new Date(this);
  result.setDate(result.getDate() + days);
  return result;
};

Date.prototype.startOfDay = function(this: Date) {
  const ret = new Date(this);

  ret.setHours(0, 0, 0, 0);

  return ret;
}

Date.prototype.isSameDayAs = function(this: Date, otherDate: Date): boolean {
  return this.getFullYear() === otherDate.getFullYear() &&
    this.getMonth() === otherDate.getMonth() &&
    this.getDate() === otherDate.getDate();
}

String.prototype.emptyCoalesce = function(this: string, value: string): string {
  return this.trim().length === 0 ? value : this;
}

String.prototype.coalesceToMaxSize = function(this: string, maxSize: number, options?: StringCoalesceToMaxSizeOptions): string {
  options ??= { };

  if (this.length <= maxSize) {
    return this;
  }

  const ellipsisString = "...";

  if ((options.addEllipsis ?? false) && maxSize > ellipsisString.length) {
    return this.substring(0, maxSize - ellipsisString.length) + ellipsisString;
  }

  return this.substring(0, maxSize);
}

String.prototype.toDisplayName = function(this: string): string {
  if (this.length === 0) return this;
  return this.replace(/([A-Z])/g, ' $1').trim();
}

Number.prototype.clamp = function(this: number, minInclusive: number, maxInclusive: number): number {
  return Math.max(minInclusive, Math.min(this, maxInclusive));
}

Number.prototype.roundToDigits = function(this: number, digits: number): number {
  const factor = Math.pow(10, digits);
  return Math.round(this * factor) / factor;
}

export {};

/**
 * Calculates the fractional number of days between two Date objects.
 * @param startDate The starting date
 * @param endDate The ending date
 * @returns The number of days between the dates as a floating point number
 */
export function getTotalDaysBetween(startDate: Date, endDate: Date): number {
  // Get the time difference in milliseconds
  const timeDifference = endDate.getTime() - startDate.getTime();

  // Convert milliseconds to days (1 day = 24 hours = 24 * 60 * 60 * 1000 milliseconds)
  return timeDifference / (24 * 60 * 60 * 1000);
}

export function minutesToMilliseconds(minutes:number): number {
  return minutes * 60 * 1000;
}

/**
 * Calculate the integer percentage of completed work out of a total.
 * Returns 0 when total is 0 or negative to avoid divide-by-zero.
 */
export function calculatePercentComplete(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((completed / total) * 100);
}

/**
 * Converts a Unix timestamp (seconds since epoch) to a Date object.
 * @param unixTimestamp The Unix timestamp in seconds
 * @returns A Date object representing the timestamp
 */
export function fromUnixTimestamp(unixTimestamp: number): Date {
  return new Date(unixTimestamp * 1000);
}

/**
 * Converts a markdown string to plain text by parsing and stripping markdown syntax.
 * @param markdown - The markdown formatted string.
 * @returns A Promise that resolves to the plain text version.
 */
export function markdownToText(markdown: string): string {
  const ast = unified().use(remarkParse).parse(markdown);  // Parse the markdown into an AST

  const processedAst = unified().use(strip).runSync(ast);

  return toString(processedAst);
}

/**
 * Extract a JSON block that is wrapped in a Markdown
 * triple‑back‑tick fence and deserialize it into the
 * desired TypeScript type.
 *
 * Works with input that has a prefix of english description followed by fenced json afterwards.
 *
 * @param fencedJson  The raw string from the LLM, e.g.
 *                    ```json { "prop": "value" } ```
 * @throws SyntaxError if the enclosed JSON is invalid
 */
export const parseFencedJson = <T>(fencedJson: string): T => {
  const fencePrefix = '```json';
  const closingFence = '```';

  // Find the last opening fence in the input
  const lastFenceIndex = fencedJson.lastIndexOf(fencePrefix);

  // Extract everything after the last opening fence
  const withoutPreviousContent = lastFenceIndex === -1 ? fencedJson : fencedJson.substring(lastFenceIndex);

  // Remove the opening fence
  const withoutOpeningFence = withoutPreviousContent.substring(fencePrefix.length);

  // With m: ^ matches the start of each line (after a newline character), and $ matches the end of each line (before a newline character).
  const closingFexRegex = new RegExp(`^\\s*${closingFence}\\s*$`, 'm');

  const closingFenceMatch = new RegExp(closingFexRegex).exec(withoutOpeningFence);
  const closingFenceIndex = closingFenceMatch ? closingFenceMatch.index : -1;

  // Remove the trailing fence: ```
  const jsonString = (
      closingFenceIndex === -1
        ? withoutOpeningFence
        : withoutOpeningFence.substring(0, closingFenceIndex)
  ).trim();

  // Parse and return with a type assertion so callers
  // get full IntelliSense on the resulting object.
  return JSON.parse(jsonString) as T;
}
export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
